import { ITodo, Todo } from '@app/models/Todo'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest } from '@app/utils/request'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export interface ITodoListState {
  readonly error?: string
  readonly filterText: string
  readonly isLoading: boolean
  readonly items: List<ITodo>
  readonly showCompleted: boolean
  readonly totalItems: number
}

const TodoListStateRecord = Record({
  error: undefined,
  filterText: '',
  isLoading: false,
  items: List<Todo>(),
  showCompleted: false,
  totalItems: 0,
})

export class TodoListState extends TodoListStateRecord
  implements ITodoListState {
  public static fromJS(initialState?: Partial<ITodoListState>) {
    if (!initialState) {
      return new TodoListState()
    }

    const { items, ...rest } = initialState
    return new TodoListState({
      items: items ? List(items.map((i) => Todo.fromJS(i))) : undefined,
      ...rest,
    })
  }

  public readonly error?: string
  public readonly filterText: string
  public readonly isLoading: boolean
  public readonly items: List<Todo>
  public readonly showCompleted: boolean
  public readonly totalItems: number

  constructor(props?: Partial<TodoListState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum TodoListActionTypes {
  LoadTodo = 'TODO_LIST::LOAD_TODO',
  LoadTodoFailure = 'TODO_LIST::LOAD_TODO_FAILURE',
  LoadTodoRequest = 'TODO_LIST::LOAD_TODO_REQUEST',
  LoadTodoSuccess = 'TODO_LIST::LOAD_TODO_SUCCESS',
  SetFilterText = 'TODO_LIST::SET_FILTER_TEXT',
  SetShowCompleted = 'TODO_LIST::SET_SHOW_COMPLETED',
}

interface ILoadTodoAction {
  append: boolean
  loadComplete?: () => void
  startIndex: number
  stopIndex: number
  type: TodoListActionTypes.LoadTodo
}

interface ILoadTodoFailureAction {
  error: string
  type: TodoListActionTypes.LoadTodoFailure
}

interface ILoadTodoRequestAction {
  type: TodoListActionTypes.LoadTodoRequest
}

interface ILoadTodoSuccessAction {
  append: boolean
  items: Todo[]
  totalItems: number
  type: TodoListActionTypes.LoadTodoSuccess
}

interface ISetFilterTextAction {
  filter: string
  type: TodoListActionTypes.SetFilterText
}

interface ISetShowCompletedAction {
  completed: boolean
  type: TodoListActionTypes.SetShowCompleted
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadTodoFailureAction
  | ILoadTodoRequestAction
  | ILoadTodoSuccessAction
  | ISetFilterTextAction
  | ISetShowCompletedAction

// -------------------------
// ACTION CREATORS
// -------------------------
function loadTodo(
  startIndex: number,
  stopIndex: number,
  append: boolean,
  loadComplete?: () => void,
): ILoadTodoAction {
  return {
    append,
    loadComplete,
    startIndex,
    stopIndex,
    type: TodoListActionTypes.LoadTodo,
  }
}

function loadTodoFailure(error: string): ILoadTodoFailureAction {
  return {
    error,
    type: TodoListActionTypes.LoadTodoFailure,
  }
}

function loadTodoRequest(): ILoadTodoRequestAction {
  return {
    type: TodoListActionTypes.LoadTodoRequest,
  }
}

function loadTodoSuccess(
  items: Todo[],
  append: boolean,
  totalItems: number,
): ILoadTodoSuccessAction {
  return {
    append,
    items,
    totalItems,
    type: TodoListActionTypes.LoadTodoSuccess,
  }
}

function setFilterText(filter: string): ISetFilterTextAction {
  return {
    filter,
    type: TodoListActionTypes.SetFilterText,
  }
}

function setShowCompleted(completed: boolean): ISetShowCompletedAction {
  return {
    completed,
    type: TodoListActionTypes.SetShowCompleted,
  }
}

export const actionCreators = {
  loadTodo,
  setFilterText,
  setShowCompleted,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<TodoListState> = (
  state: TodoListState = new TodoListState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case TodoListActionTypes.LoadTodoFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as TodoListState

    case TodoListActionTypes.LoadTodoRequest:
      return state.set('isLoading', true) as TodoListState

    case TodoListActionTypes.LoadTodoSuccess:
      const newItems = action.append
        ? (state.items.concat(List(action.items)) as List<Todo>)
        : List(action.items)
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('items', newItems)
        .set('totalItems', action.totalItems) as TodoListState

    case TodoListActionTypes.SetFilterText:
      return state.set('filterText', action.filter) as TodoListState

    case TodoListActionTypes.SetShowCompleted:
      return state.set('showCompleted', action.completed) as TodoListState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadTodoSaga(action: ILoadTodoAction) {
  try {
    yield put(loadTodoRequest())

    const currentState: TodoListState = yield select(
      (state: ApplicationState) => state.todoList,
    )

    const query: string[] = []

    if (currentState.filterText) {
      query.push(`filter=${encodeURIComponent(currentState.filterText)}`)
    }

    if (currentState.showCompleted) {
      query.push(`includeCompleted=${currentState.showCompleted}`)
    }

    if (action.startIndex > 0) {
      query.push(`skip=${action.startIndex}`)
    }

    const count = action.stopIndex - action.startIndex + 1
    if (count > 0) {
      query.push(`count=${count}`)
    }

    const baseUrl = '/api/todos'

    const url = `${baseUrl}${query.length > 0 ? `?${query.join('&')}` : ''}`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      const contacts: Todo[] = result.results.map((c: any) => Todo.fromJS(c))
      yield put(loadTodoSuccess(contacts, action.append, 0))
    } else {
      yield put(loadTodoFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadTodoFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadTodo() {
  yield takeLatest(TodoListActionTypes.LoadTodo, loadTodoSaga)
}

export function* todoListRootSaga() {
  yield fork(watchLoadTodo)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState): TodoListState =>
  state.todoList

export const selectError = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.error,
  )

export const selectFilterText = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.filterText,
  )

export const selectIsLoading = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isLoading,
  )

export const selectItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.items,
  )

export const selectShowCompleted = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.showCompleted,
  )

export const selectTotalItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.totalItems,
  )
